
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommentsEstablishmentService } from './service/comments-establishment.service';
import { CommentsData, CommentsResponse } from './interfaces/comments-establishment.interface';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'comments-establishment',
  imports: [NgxSkeletonLoaderModule],
  templateUrl: './comments-establishment.component.html',
  styleUrl: './comments-establishment.component.css',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CommentsEstablishmentComponent implements OnInit, OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();
  IdRestaurant: Number = 0;
  comments: CommentsData[] = [];
  isLoading: boolean = false;
  hasError: boolean = false;
  
  // Variables de paginación
  currentPage: number = 1;
  totalPages: number = 1;
  perPage: number = 5; // Reducir a 5 comentarios por página
  total: number = 0;
  Math = Math; // Para usar Math en el template
  
  // Variables para "cargar más"
  allComments: CommentsData[] = []; // Todos los comentarios cargados
  hasMoreComments: boolean = false; // Si hay más comentarios por cargar
  isLoadingMore: boolean = false; // Estado de carga para "cargar más"
  
  @Input() establishmentId: string | null = null;
  constructor(
    private route: ActivatedRoute,
    private commentsEstablishmentService: CommentsEstablishmentService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // console.log('CommentsEstablishmentComponent - Input establishmentId:', this.establishmentId);
    // Si el ID ya está disponible a través del Input, usarlo
    if (this.establishmentId) {
      this.IdRestaurant = Number(this.establishmentId);
      // console.log('CommentsEstablishmentComponent - Cargando comentarios con ID:', this.IdRestaurant);
      this.loadComments();
    } else {
      // Como fallback, intentar obtenerlo de la ruta
      this.route.params.subscribe(params => {
        const id = params['id'];
        if (id) {
          this.IdRestaurant = Number(id);
          this.loadComments();
        }
      });
    }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    // console.log('CommentsEstablishmentComponent - ngOnChanges:', changes);
    // Recargar comentarios cuando cambie el ID del establecimiento
    if (changes['establishmentId'] && changes['establishmentId'].currentValue) {
      this.IdRestaurant = Number(changes['establishmentId'].currentValue);
      // console.log('CommentsEstablishmentComponent - Cargando comentarios con ID (desde cambios):', this.IdRestaurant);
      this.loadComments();
    }
  }


  loadComments(page: number = 1) {
    // console.log('CommentsEstablishmentComponent - Método loadComments con ID:', this.IdRestaurant, 'Página:', page);
    this.isLoading = true;
    this.hasError = false;
    
    const commentsRequest = {
      IdRestaurant: Number(this.IdRestaurant),
      page: page,
      perPage: this.perPage
    };
    
    this.commentsEstablishmentService.loadComments(commentsRequest)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
         next: (response: any) => {
            // console.log('Respuesta completa del servidor:', response);
            
            // Manejar la estructura anidada de la respuesta
            if (response.data && response.data.data) {
              const newComments = response.data.data || [];
              
              // Generar IDs únicos para cada comentario
              const commentsWithIds = newComments.map((comment: any, index: number) => ({
                ...comment,
                id: `${Date.now()}-${page}-${index}-${comment.ua_name}-${comment.comment.substring(0, 10)}`
              }));
              
              // Si es la primera página, reemplazar; si no, agregar
              if (page === 1) {
                this.allComments = commentsWithIds;
                this.comments = commentsWithIds;
              } else {
                this.allComments = [...this.allComments, ...commentsWithIds];
                this.comments = this.allComments;
              }
              
              // Manejar paginación desde response.data
              if (response.data.current_page !== undefined) {
                this.currentPage = response.data.current_page;
                this.totalPages = response.data.last_page || 1;
                this.total = response.data.total || 0;
                this.perPage = response.data.per_page || 5;
                this.hasMoreComments = this.currentPage < this.totalPages;
              } else {
                // Si no hay paginación en response.data, asumir que hay más si se cargaron comentarios
                this.hasMoreComments = newComments.length >= this.perPage;
              }
            } else if (response.data) {
              const newComments = response.data || [];
              if (page === 1) {
                this.allComments = newComments;
                this.comments = newComments;
              } else {
                this.allComments = [...this.allComments, ...newComments];
                this.comments = this.allComments;
              }
              // Si no hay estructura de paginación, asumir que hay más si se cargaron comentarios
              this.hasMoreComments = newComments.length >= this.perPage;
            } else {
              this.comments = [];
              this.allComments = [];
              this.hasMoreComments = false;
            }
            
            // Validar que la paginación exista en la respuesta (formato alternativo)
            if (response.pagination) {
              this.currentPage = response.pagination.currentPage || 1;
              this.totalPages = response.pagination.totalPages || 1;
              this.total = response.pagination.total || 0;
              this.perPage = response.pagination.perPage || 10;
              this.hasMoreComments = this.currentPage < this.totalPages;
            }
            
            // Si no hay información de paginación específica, usar valores por defecto
            if (!response.data?.current_page && !response.pagination) {
              this.currentPage = page;
              this.totalPages = this.hasMoreComments ? page + 1 : page;
              this.total = this.comments.length;
              this.perPage = 5;
            }
            
            this.hasError = false;
            // console.log('Comentarios procesados:', this.comments);
            // console.log('Información de paginación:', {
            //   currentPage: this.currentPage,
            //   totalPages: this.totalPages,
            //   total: this.total,
            //   perPage: this.perPage
            // });
          },
        error: (error) => {
          console.error('Error al cargar los comentarios:', error);
          this.hasError = true;
        }
      });
  }
  
  // Método para cargar más comentarios
  loadMoreComments() {
    if (this.hasMoreComments && !this.isLoadingMore) {
      this.isLoadingMore = true;
      
      const nextPage = this.currentPage + 1;
      const commentsRequest = {
        IdRestaurant: Number(this.IdRestaurant),
        page: nextPage,
        perPage: this.perPage
      };
      
      this.commentsEstablishmentService.loadComments(commentsRequest)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.isLoadingMore = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe({
          next: (response: any) => {
            // console.log('Respuesta de cargar más:', response);
            
            if (response.data && response.data.data) {
              const newComments = response.data.data || [];
              
              // Generar IDs únicos para cada comentario
              const commentsWithIds = newComments.map((comment: any, index: number) => ({
                ...comment,
                id: `${Date.now()}-${nextPage}-${index}-${comment.ua_name}-${comment.comment.substring(0, 10)}`
              }));
              
              this.allComments = [...this.allComments, ...commentsWithIds];
              this.comments = this.allComments;
              
              if (response.data.current_page !== undefined) {
                this.currentPage = response.data.current_page;
                this.totalPages = response.data.last_page || 1;
                this.hasMoreComments = this.currentPage < this.totalPages;
              } else {
                // Si no hay paginación, verificar si se cargaron comentarios
                this.currentPage = nextPage;
                this.hasMoreComments = newComments.length >= this.perPage;
              }
            } else if (response.data) {
              const newComments = response.data || [];
              this.allComments = [...this.allComments, ...newComments];
              this.comments = this.allComments;
              this.currentPage = nextPage;
              this.hasMoreComments = newComments.length >= this.perPage;
            } else {
              // No hay más comentarios
              this.hasMoreComments = false;
            }
            
            // Manejar paginación alternativa
            if (response.pagination) {
              this.currentPage = response.pagination.currentPage || nextPage;
              this.totalPages = response.pagination.totalPages || 1;
              this.total = response.pagination.total || 0;
              this.hasMoreComments = this.currentPage < this.totalPages;
            }
          },
          error: (error) => {
            console.error('Error al cargar más comentarios:', error);
          }
        });
    }
  }

   ngOnDestroy(): void {
     this.destroy$.next();
     this.destroy$.complete();
   }
 }
